import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ActivityIndicator,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { Button } from './Button';
import { Input } from './Input';
import { Colors, Fonts, Radii, StatusTints, Typography } from '../constants';
import { useUser, useDialog } from '../contexts';
import { accountDeletionService, AccountDeletionPreview, ClubTransfer } from '../services/accountDeletion';

interface DeleteAccountFormProps {
  onCancel: () => void;
}

type Step = 'warning' | 'clubs' | 'confirm';

const ImpactRow: React.FC<{
  icon: keyof typeof Ionicons.glyphMap;
  text: string;
  danger?: boolean;
}> = ({ icon, text, danger }) => (
  <View style={styles.impactRow}>
    <Ionicons
      name={icon}
      size={20}
      color={danger ? Colors.error : Colors.textMedium}
      style={styles.impactIcon}
    />
    <Text style={[styles.impactText, danger && styles.impactTextDanger]}>{text}</Text>
  </View>
);

/** Circular step badge: soft danger tint with the step's icon in danger ink */
const StepBadge: React.FC<{ icon: keyof typeof Ionicons.glyphMap }> = ({ icon }) => (
  <View style={styles.stepBadge}>
    <Ionicons name={icon} size={26} color={Colors.error} />
  </View>
);

/** Three-segment progress bar across the delete flow */
const StepProgress: React.FC<{ index: number }> = ({ index }) => (
  <View style={styles.progress}>
    {[0, 1, 2].map((i) => (
      <View key={i} style={[styles.progressSegment, i <= index && styles.progressSegmentOn]} />
    ))}
  </View>
);

export const DeleteAccountForm: React.FC<DeleteAccountFormProps> = ({ onCancel }) => {
  const { t } = useTranslation();
  const { user } = useUser();
  const dialog = useDialog();

  const [step, setStep] = useState<Step>('warning');
  const [preview, setPreview] = useState<AccountDeletionPreview | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(true);

  // Club transfer state: club_id → selected member user_id
  const [clubTransfers, setClubTransfers] = useState<Record<string, string>>({});

  // Confirm step state
  const [emailInput, setEmailInput] = useState('');
  const [checkboxChecked, setCheckboxChecked] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    accountDeletionService.getPreview()
      .then(setPreview)
      .catch(() => dialog.alert(t('common.errors.unknown'), '', 'error'))
      .finally(() => setLoadingPreview(false));
  }, []);

  const handleStep1Continue = () => {
    if (preview && preview.sole_managed_clubs.length > 0) {
      setStep('clubs');
    } else {
      setStep('confirm');
    }
  };

  const handleStep2Continue = () => {
    // Validate all sole-managed clubs have a transfer selected
    const allSelected = preview!.sole_managed_clubs.every(
      (club) => clubTransfers[club.club_id]
    );
    if (!allSelected) {
      dialog.alert(
        t('screens.deleteAccount.step2Title'),
        t('screens.deleteAccount.step2AllRequired'),
        'error'
      );
      return;
    }
    setStep('confirm');
  };

  const handleDelete = async () => {
    if (!user || emailInput.trim().toLowerCase() !== user.email.toLowerCase()) {
      dialog.alert(
        t('screens.deleteAccount.errorTitle'),
        t('screens.deleteAccount.emailMismatch'),
        'error'
      );
      return;
    }

    const transfers: ClubTransfer[] = Object.entries(clubTransfers).map(
      ([club_id, new_manager_id]) => ({ club_id, new_manager_id })
    );

    setDeleting(true);
    try {
      await accountDeletionService.deleteAccount(transfers);
      // Auth state listener will sign out and navigate to guest screens
    } catch (error: any) {
      setDeleting(false);
      dialog.alert(
        t('screens.deleteAccount.errorTitle'),
        error.message || t('common.errors.unknown'),
        'error'
      );
    }
  };

  const canDelete =
    emailInput.trim().toLowerCase() === (user?.email ?? '').toLowerCase() &&
    checkboxChecked &&
    !deleting;

  return (
    <View style={styles.inner}>
      <StepProgress index={step === 'warning' ? 0 : step === 'clubs' ? 1 : 2} />

      {/* ── Step 1: Warning ────────────────────────────── */}
      {step === 'warning' && (
        <>
          <StepBadge icon="warning-outline" />
          <Text style={styles.stepTitle}>{t('screens.deleteAccount.step1Title')}</Text>
          <Text style={styles.stepSubtitle}>{t('screens.deleteAccount.step1Subtitle')}</Text>

          {loadingPreview ? (
            <ActivityIndicator color={Colors.primary} style={{ marginVertical: 24 }} />
          ) : (
            <View style={styles.impactCard}>
              {(preview?.active_listings_count ?? 0) > 0 && (
                <ImpactRow
                  icon="trash-outline"
                  text={`${preview!.active_listings_count} ${t('screens.deleteAccount.impactListings')}`}
                  danger
                />
              )}
              {(preview?.active_transactions_count ?? 0) > 0 && (
                <ImpactRow
                  icon="close-circle-outline"
                  text={`${preview!.active_transactions_count} ${t('screens.deleteAccount.impactTransactions')}`}
                  danger
                />
              )}
              {(preview?.sole_managed_clubs.length ?? 0) > 0 && (
                <ImpactRow
                  icon="ribbon-outline"
                  text={`${preview!.sole_managed_clubs.length} ${t('screens.deleteAccount.impactClubs')}`}
                  danger
                />
              )}
              <ImpactRow icon="chatbubble-ellipses-outline" text={t('screens.deleteAccount.impactMessages')} />
              <ImpactRow icon="lock-closed-outline" text={t('screens.deleteAccount.impactData')} />
            </View>
          )}

          <View style={styles.buttons}>
            <Button
              title={t('common.buttons.cancel')}
              onPress={onCancel}
              variant="outline"
              style={styles.buttonHalf}
            />
            <Button
              title={t('screens.deleteAccount.continueButton')}
              onPress={handleStep1Continue}
              variant="danger"
              disabled={loadingPreview}
              style={styles.buttonHalf}
            />
          </View>
        </>
      )}

      {/* ── Step 2: Club transfers ──────────────────────── */}
      {step === 'clubs' && preview && (
        <>
          <StepBadge icon="ribbon-outline" />
          <Text style={styles.stepTitle}>{t('screens.deleteAccount.step2Title')}</Text>
          <Text style={styles.stepSubtitle}>{t('screens.deleteAccount.step2Subtitle')}</Text>

          {preview.sole_managed_clubs.map((club) => (
            <View key={club.club_id} style={styles.clubTransferCard}>
              <Text style={styles.clubTransferName}>{club.club_name}</Text>
              <Text style={styles.clubTransferLabel}>
                {t('screens.deleteAccount.selectNewManager')}
              </Text>
              {club.members.length === 0 ? (
                <Text style={styles.noMembersText}>
                  {t('screens.deleteAccount.noMembers')}
                </Text>
              ) : (
                club.members.map((member) => (
                  <TouchableOpacity
                    key={member.user_id}
                    style={[
                      styles.memberRow,
                      clubTransfers[club.club_id] === member.user_id && styles.memberRowSelected,
                    ]}
                    onPress={() =>
                      setClubTransfers((prev) => ({
                        ...prev,
                        [club.club_id]: member.user_id,
                      }))
                    }
                  >
                    <View style={[
                      styles.radioCircle,
                      clubTransfers[club.club_id] === member.user_id && styles.radioCircleSelected,
                    ]} />
                    <Text style={styles.memberName}>{member.display_name}</Text>
                  </TouchableOpacity>
                ))
              )}
            </View>
          ))}

          <View style={styles.buttons}>
            <Button
              title={t('common.buttons.cancel')}
              onPress={onCancel}
              variant="outline"
              style={styles.buttonHalf}
            />
            <Button
              title={t('screens.deleteAccount.step2Continue')}
              onPress={handleStep2Continue}
              variant="danger"
              style={styles.buttonHalf}
            />
          </View>
        </>
      )}

      {/* ── Step 3: Final confirmation ──────────────────── */}
      {step === 'confirm' && (
        <>
          <StepBadge icon="trash-outline" />
          <Text style={styles.stepTitle}>{t('screens.deleteAccount.step3Title')}</Text>
          <Text style={styles.stepSubtitle}>{t('screens.deleteAccount.step3Subtitle')}</Text>

          <Input
            label={t('screens.deleteAccount.emailLabel')}
            value={emailInput}
            onChangeText={setEmailInput}
            placeholder={user?.email ?? ''}
            autoCapitalize="none"
            keyboardType="email-address"
            autoComplete="off"
            style={styles.emailInput}
          />

          <TouchableOpacity
            style={styles.checkboxRow}
            onPress={() => setCheckboxChecked((v) => !v)}
            activeOpacity={0.7}
          >
            <View style={[styles.checkbox, checkboxChecked && styles.checkboxChecked]}>
              {checkboxChecked && <Text style={styles.checkboxTick}>✓</Text>}
            </View>
            <Text style={styles.checkboxLabel}>
              {t('screens.deleteAccount.checkboxLabel')}
            </Text>
          </TouchableOpacity>

          <View style={styles.buttons}>
            <Button
              title={t('common.buttons.cancel')}
              onPress={onCancel}
              variant="outline"
              disabled={deleting}
              style={styles.buttonHalf}
            />
            <Button
              title={deleting ? t('screens.deleteAccount.deleting') : t('screens.deleteAccount.finalButton')}
              onPress={handleDelete}
              variant="danger"
              disabled={!canDelete}
              loading={deleting}
              style={styles.buttonHalf}
            />
          </View>
        </>
      )}

    </View>
  );
};

const styles = StyleSheet.create({
  inner: {
    padding: 16,
  },
  progress: {
    flexDirection: 'row',
    gap: 6,
    marginBottom: 20,
  },
  progressSegment: {
    flex: 1,
    height: 4,
    borderRadius: 2,
    backgroundColor: Colors.borderLight,
  },
  progressSegmentOn: {
    backgroundColor: Colors.error,
  },
  stepBadge: {
    width: 52,
    height: 52,
    borderRadius: Radii.card,
    backgroundColor: StatusTints.error.bg,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 14,
  },
  stepTitle: {
    ...Typography.section,
    color: Colors.textDark,
    marginBottom: 4,
  },
  stepSubtitle: {
    fontFamily: Fonts.regular,
    fontSize: 14,
    lineHeight: 21,
    color: Colors.textMedium,
    marginBottom: 20,
  },
  impactCard: {
    backgroundColor: Colors.white,
    borderRadius: Radii.card,
    padding: 16,
    marginBottom: 24,
    gap: 12,
    borderWidth: 1,
    borderColor: Colors.borderLight,
  },
  impactRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
  },
  impactIcon: {
    flexShrink: 0,
    marginTop: 1,
  },
  impactText: {
    flex: 1,
    fontFamily: Fonts.regular,
    fontSize: 15,
    color: Colors.text,
    lineHeight: 22,
  },
  impactTextDanger: {
    fontFamily: Fonts.semiBold,
    color: Colors.error,
    fontWeight: '600',
  },
  buttons: {
    flexDirection: 'row',
    gap: 12,
  },
  buttonHalf: {
    flex: 1,
  },
  clubTransferCard: {
    backgroundColor: Colors.white,
    borderRadius: Radii.card,
    padding: 16,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: Colors.borderLight,
  },
  clubTransferName: {
    fontFamily: Fonts.semiBold,
    fontSize: 16,
    lineHeight: 21,
    fontWeight: '600',
    color: Colors.textDark,
    marginBottom: 2,
  },
  clubTransferLabel: {
    fontFamily: Fonts.regular,
    fontSize: 13,
    color: Colors.textMedium,
    marginBottom: 12,
  },
  memberRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: Radii.control,
    borderWidth: 1,
    borderColor: Colors.borderLight,
    marginBottom: 8,
  },
  memberRowSelected: {
    borderColor: Colors.primary,
    backgroundColor: Colors.primaryLight,
  },
  radioCircle: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: Colors.border,
  },
  radioCircleSelected: {
    borderColor: Colors.primary,
    backgroundColor: Colors.primary,
  },
  memberName: {
    fontFamily: Fonts.regular,
    fontSize: 15,
    color: Colors.text,
  },
  noMembersText: {
    fontSize: 14,
    color: Colors.textMedium,
    fontStyle: 'italic',
  },
  emailInput: {
    marginBottom: 16,
  },
  checkboxRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    marginBottom: 24,
  },
  checkbox: {
    width: 22,
    height: 22,
    borderRadius: 4,
    borderWidth: 2,
    borderColor: Colors.border,
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 1,
    flexShrink: 0,
  },
  checkboxChecked: {
    backgroundColor: Colors.error,
    borderColor: Colors.error,
  },
  checkboxTick: {
    color: Colors.white,
    fontSize: 14,
    fontWeight: '700',
  },
  checkboxLabel: {
    flex: 1,
    fontFamily: Fonts.regular,
    fontSize: 14,
    color: Colors.text,
    lineHeight: 21,
  },
});
